import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export const toastVariants = cva(
  'absolute inset-x-0 group overflow-hidden bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-700 rounded-lg shadow-lg p-4 flex gap-2.5 transition-transform duration-300 ease-in-out min-h-[60px] pointer-events-auto focus:outline-none items-start',
  {
    variants: {
      position: {
        'top-left':
          'data-[state=open]:animate-[toast-slide-in-left_300ms_ease-out] data-[state=closed]:animate-[toast-closed-slide-up_500ms_ease-in-out]',
        'top-center':
          'data-[state=open]:animate-[toast-slide-in-top_300ms_ease-out] data-[state=closed]:animate-[toast-closed-slide-up_500ms_ease-in-out]',
        'top-right':
          'data-[state=open]:animate-[toast-slide-in-right_300ms_ease-out] data-[state=closed]:animate-[toast-closed-slide-up_500ms_ease-in-out]',
        'bottom-left':
          'data-[state=open]:animate-[toast-slide-in-left_300ms_ease-out] data-[state=closed]:animate-[toast-closed-slide-down_500ms_ease-in-out]',
        'bottom-center':
          'data-[state=open]:animate-[toast-slide-in-bottom_300ms_ease-out] data-[state=closed]:animate-[toast-closed-slide-down_500ms_ease-in-out]',
        'bottom-right':
          'data-[state=open]:animate-[toast-slide-in-right_300ms_ease-out] data-[state=closed]:animate-[toast-closed-slide-down_500ms_ease-in-out]',
      },
      orientation: {
        horizontal: 'items-start',
        vertical: 'items-start',
      },
      title: {
        true: {
          description: 'mt-1',
        },
      },
    },
  },
);

export const toastIconVariants = cva('flex-shrink-0 flex items-center justify-center', {
  variants: {
    color: {
      primary: 'text-blue-600',
      success: 'text-green-600',
      warning: 'text-orange-600',
      error: 'text-red-600',
      neutral: 'text-gray-400',
    },
    size: {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    },
  },
  compoundVariants: [
    // Different margins for different sizes in horizontal layout only
    {
      size: 'sm',
      class: 'mt-[2.5px]',
    },
    {
      size: 'md',
      class: 'mt-[1px]',
    },
    {
      size: 'lg',
      class: '-mt-[1.5px]',
    },
  ],
});

export const toastContentVariants = cva('w-0 flex-1 flex flex-col');

export const toastTitleVariants = cva('text-sm font-semibold text-gray-900 dark:text-white');

export const toastDescriptionVariants = cva('text-sm text-gray-600 dark:text-gray-400');

export const toastActionsVariants = cva('flex gap-3 shrink-0', {
  variants: {
    orientation: {
      horizontal: 'items-center',
      vertical: 'items-center mt-2.5',
    },
  },
});

export const toastCloseVariants = cva('p-1');

export const toastProgressVariants = cva('absolute inset-x-0 bottom-0');

export type KaToastVariants = VariantProps<typeof toastVariants>;
export type KaToastIconVariants = VariantProps<typeof toastIconVariants>;
export type KaToastColor = NonNullable<KaToastIconVariants['color']>;
export type KaToastIconSize = NonNullable<KaToastIconVariants['size']>;
export type KaToastPosition = NonNullable<KaToastVariants['position']>;
export type KaToastOrientation = NonNullable<KaToastVariants['orientation']>;
